import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search, MapPin, Plus, Megaphone, Calendar } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState, Pagination, DemoBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const ALL = "__all__";

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Opportunities() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const category = params.get("category") || "";
  const page = parseInt(params.get("page") || "1", 10);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "", location: "" });
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setCats(data.opportunity_categories)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities", {
        params: { search: search || undefined, category: category || undefined, page },
      });
      setData(data);
    } catch {
      setData({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [search, category, page]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("page" in patch)) next.set("page", "1");
    setParams(next);
  };

  const post = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.category) {
      toast.error("Please fill in title, category and description.");
      return;
    }
    setPosting(true);
    try {
      await api.post("/opportunities", form);
      toast.success("Opportunity posted!");
      setOpen(false);
      setForm({ title: "", description: "", category: "", location: "" });
      update({ page: "1", search: "" });
      setSearch("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="container-nx section-nx">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="label-eyebrow mb-2">Opportunities</div>
          <h1 className="heading-2">Business opportunities & requirements</h1>
          <p className="mt-3 max-w-2xl text-base text-slate-500">
            Browse requirements, partnerships, supplier needs and referrals from the community.
          </p>
        </div>
        <button onClick={() => (authed ? setOpen(true) : navigate("/login"))} className="btn-gold shrink-0" data-testid="post-opportunity-btn">
          <Plus className="h-4 w-4" /> Post opportunity
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && update({ search })}
            placeholder="Search opportunities..." className="input-nx pl-10" data-testid="opportunity-search-input" />
        </div>
        <Select value={category || ALL} onValueChange={(v) => update({ category: v === ALL ? "" : v })}>
          <SelectTrigger className="input-nx h-auto sm:w-72" data-testid="opportunity-category-select">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => update({ search })} className="btn-navy" data-testid="opportunity-search-btn">Search</button>
      </div>

      <div className="mt-8">
        {loading || !data ? (
          <Spinner label="Loading opportunities..." />
        ) : data.items.length === 0 ? (
          <EmptyState title="No opportunities yet" message="Be the first to post a requirement, partnership or referral." icon={Megaphone}
            action={<button onClick={() => (authed ? setOpen(true) : navigate("/login"))} className="btn-gold"><Plus className="h-4 w-4" /> Post opportunity</button>} />
        ) : (
          <>
            <div className="space-y-4">
              {data.items.map((o) => (
                <Link key={o.id} to={`/opportunities/${o.id}`} className="card-nx card-nx-hover block p-6" data-testid={`opportunity-card-${o.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <span className="chip-gold">{o.category}</span>
                      <h3 className="mt-3 text-lg font-semibold text-[#0B192C] font-head">{o.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-slate-500">{o.description}</p>
                    </div>
                    {o.is_demo && <DemoBadge />}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                    <span>By {o.author_name}</span>
                    {o.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{o.location}</span>}
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{timeAgo(o.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => update({ page: String(p) })} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Post an opportunity</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Title *" className="input-nx" data-testid="opp-title-input" />
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="opp-category-select"><SelectValue placeholder="Select category *" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="Location (optional)" className="input-nx" data-testid="opp-location-input" />
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describe the opportunity... *" className="min-h-[120px]" data-testid="opp-description-input" />
          </div>
          <DialogFooter>
            <button onClick={post} disabled={posting} className="btn-gold" data-testid="opp-submit-btn">
              {posting ? "Posting..." : "Post opportunity"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
